import CustomTooltip from '../../../_components/CustomTooltip'
import { TypeFilterProps } from './extra/types'
import { useEffect, useMemo, useRef, useState } from 'react'

import InfoIcon from '@mui/icons-material/Info'
import Chip from '@mui/material/Chip'

/** @type{ React.FC<import("zod/mini").infer<TypeFilterProps>>} */
export const TypeFilter = (_props) => {
  const {
    onFilter,
    onSelectItem,
    recordCountGetter,
    types,
    selectedItemCode,
    title,
    registerButton,
    onShowMemoModal,
    statusGetter,
    itemErrorGetter
  } = TypeFilterProps.parse(_props)

  const [filterText, setFilterText] = useState('')

  /**
   * @function handleFilterChange
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  const handleFilterChange = (e) => {
    const text = e.target.value
    setFilterText(text)
    onFilter?.(text)
  }

  const _filteredTypes = useMemo(() => {
    if (filterText === '') return types
    const filterTextLower = filterText.toLowerCase()
    return types.filter((e) => e.label.toLowerCase().includes(filterTextLower))
  }, [filterText, types])

  const isFilteredTypesEmpty = _filteredTypes.length === 0 && Boolean(filterText)

  const refs = useRef([])

  useHandleKeyboardEvents(refs.current)

  return (
    <>
      <div className=" flex p-3 px-5 h-[70px] justify-between items-start base-table-header">
        <h2 className="font-medium ">{title}</h2>
        {registerButton}
      </div>
      <div className="overflow-y-auto  border-t border-gray-300">
        <div className=" relative">
          <div className="p-3 sticky top-0  z-10">
            <input
              data-testid="filterText"
              type="text"
              name="filterText"
              value={filterText}
              placeholder={'区分を検索'}
              onChange={handleFilterChange}
              className="rounded-lg text-input w-full text-left"
            />
          </div>
          <div className="p-3 pt-0">
            {isFilteredTypesEmpty && <p className="p-2 text-base text-center">一致する結果がありませんでした</p>}
            {_filteredTypes.map((e, index) => {
              const selected = e.code === selectedItemCode
              const status = statusGetter?.(e.code)
              const hasError = itemErrorGetter?.(e.code) ?? false
              const rowSurface = hasError
                ? selected
                  ? 'bg-red-200 hover:bg-red-200/90 ring-2 ring-inset ring-red-300/80'
                  : 'bg-red-100 hover:bg-red-200/90'
                : selected
                  ? 'bg-gray-300/80'
                  : 'hover:bg-gray-300/80'
              return (
                <div
                  ref={(el) => {
                    if (el) refs.current[index] = el
                  }}
                  tabIndex={0}
                  data-testid={`typeFilterItem_${e.code}`}
                  role="button"
                  onClick={() => onSelectItem(e)}
                  className={`flex gap-1 text-base mb-1 p-2 rounded cursor-pointer ${rowSurface}`}
                  key={index}
                >
                  <CustomTooltip title={e.label}>
                    {(ref) => (
                      <div ref={ref} className="flex-1 font-medium truncate">
                        {e.label}
                      </div>
                    )}
                  </CustomTooltip>

                  <div className=" justify-between font-normal inline-flex items-center gap-1">
                    {e?.isDeleted && <Chip color="warning" label={'削除待ち'} size="small" className="mr-3" />}
                    {status === 'pending' && <span className="text-orange-500 text-xs font-medium mr-1">未反映</span>}
                    {status === 'success' && <span className="text-green-500 text-xs font-medium mr-1">更新完了</span>}
                    {status === 'error' && <span className="text-red-500 text-xs font-medium mr-1">更新エラー</span>}

                    <CustomTooltip title="区分の説明を表示">
                      {(ref) => (
                        <button
                          ref={ref}
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation()
                            onShowMemoModal?.(e)
                          }}
                          className="flex items-center justify-center p-0.5 hover:bg-gray-200 rounded transition-colors"
                          aria-label="区分の説明を表示"
                        >
                          {e.icon || <InfoIcon sx={{ color: '#297AD8' }} fontSize="14px" />}
                        </button>
                      )}
                    </CustomTooltip>
                    <span className="text-right text-sm -translate-y-[1px]"> {recordCountGetter(e.code) ?? 0}件</span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </>
  )
}

const useHandleKeyboardEvents = (refs) => {
  useEffect(() => {
    const control = new AbortController()

    if (!refs?.length) return

    window.addEventListener(
      'keydown',
      (ev) => {
        refs.forEach((e) => {
          if (document.activeElement === e && ev.key === 'Enter') {
            console.log(e)
            e.click()
          }
        })
      },
      { signal: control.signal }
    )

    return () => control.abort()
  }, [])
}
